/**
 * ManualAdminPage Component
 *
 * Admin interface for managing handbook sections.
 * Story 7-4
 */

import { useState } from 'react';
import { Layout } from '../../components/Layout';
import { SectionEditor } from './SectionEditor';
import { PublishVersionModal } from './PublishVersionModal';
import {
  useAdminSections,
  useCreateSection,
  useUpdateSection,
  useDeleteSection,
  usePublishVersion,
} from './useManualAdmin';
import type { ManualSection, CreateSectionInput, UpdateSectionInput } from '../../lib/manualApi';

type ModalState =
  | { type: 'none' }
  | { type: 'create' }
  | { type: 'edit'; section: ManualSection }
  | { type: 'publish' };

export function ManualAdminPage() {
  const { data: sections, isLoading, error } = useAdminSections();
  const createMutation = useCreateSection();
  const updateMutation = useUpdateSection();
  const deleteMutation = useDeleteSection();
  const publishMutation = usePublishVersion();

  const [modalState, setModalState] = useState<ModalState>({ type: 'none' });

  // Handlers
  const handleCreate = () => {
    setModalState({ type: 'create' });
  };

  const handleEdit = (section: ManualSection) => {
    setModalState({ type: 'edit', section });
  };

  const handleDelete = async (section: ManualSection) => {
    if (!confirm(`Weet je zeker dat je "${section.title}" wilt verwijderen?`)) {
      return;
    }

    try {
      await deleteMutation.mutateAsync(section.id);
    } catch (err) {
      console.error('Delete error:', err);
      alert('Fout bij verwijderen. Probeer het opnieuw.');
    }
  };

  const handleToggleActive = async (section: ManualSection) => {
    try {
      await updateMutation.mutateAsync({
        id: section.id,
        data: { isActive: !section.isActive },
      });
    } catch (err) {
      console.error('Toggle error:', err);
      alert('Fout bij wijzigen status. Probeer het opnieuw.');
    }
  };

  const handleSaveSection = async (data: CreateSectionInput | UpdateSectionInput) => {
    try {
      if (modalState.type === 'create') {
        await createMutation.mutateAsync(data as CreateSectionInput);
      } else if (modalState.type === 'edit') {
        await updateMutation.mutateAsync({
          id: modalState.section.id,
          data: data as UpdateSectionInput,
        });
      }
      setModalState({ type: 'none' });
    } catch (err) {
      console.error('Save error:', err);
      throw err; // Re-throw to let the editor handle it
    }
  };

  const handlePublish = async (versionNumber: string, changelog: string) => {
    try {
      await publishMutation.mutateAsync({ versionNumber, changelog });
      setModalState({ type: 'none' });
      alert('Nieuwe versie succesvol gepubliceerd!');
    } catch (err) {
      console.error('Publish error:', err);
      throw err; // Re-throw to let the modal handle it
    }
  };

  const closeModal = () => {
    setModalState({ type: 'none' });
  };

  // Render helpers
  const getVisibilityLabel = (visibility: string) => {
    const labels: Record<string, string> = {
      all: 'Alle rollen',
      medewerker: 'Medewerker',
      beoordelaar: 'Beoordelaar',
      hr: 'HR',
    };
    return labels[visibility] || visibility;
  };

  return (
    <Layout>
      <div data-testid="manual-admin-page">
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Handleiding Beheer</h1>
            <p className="mt-1 text-sm text-gray-500">
              Beheer handleiding secties en publiceer nieuwe versies
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex gap-3">
            <button
              onClick={() => setModalState({ type: 'publish' })}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="mr-2 -ml-1 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Publiceer Versie
            </button>
            <button
              onClick={handleCreate}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="mr-2 -ml-1 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Nieuwe Sectie
            </button>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <svg className="h-5 w-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-red-700">
                Fout bij het laden van secties. Probeer het opnieuw.
              </p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Laden...</span>
          </div>
        )}

        {/* Sections Table */}
        {!isLoading && sections && (
          <>
            {/* Results Count */}
            <div className="mb-4 text-sm text-gray-500">
              {sections.length} {sections.length === 1 ? 'sectie' : 'secties'}
            </div>

            {/* Table */}
            {sections.length > 0 ? (
              <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Titel
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Slug
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Zichtbaarheid
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Volgorde
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Dynamisch
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acties
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sections.map((section) => (
                      <tr key={section.id} className={!section.isActive ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'}>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {section.title}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-500 font-mono">
                            {section.slug}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {getVisibilityLabel(section.visibility)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {section.sortOrder}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {section.hasDynamicData ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              Ja
                            </span>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => handleToggleActive(section)}
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              section.isActive
                                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {section.isActive ? 'Actief' : 'Inactief'}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleEdit(section)}
                            className="text-blue-600 hover:text-blue-900 mr-4"
                          >
                            Bewerken
                          </button>
                          <button
                            onClick={() => handleDelete(section)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Verwijderen
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-12 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
                <h3 className="mt-4 text-lg font-medium text-gray-900">
                  Geen secties gevonden
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Begin met het toevoegen van handleiding secties.
                </p>
                <button
                  onClick={handleCreate}
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  Nieuwe Sectie
                </button>
              </div>
            )}
          </>
        )}

        {/* Modals */}
        {(modalState.type === 'create' || modalState.type === 'edit') && (
          <SectionEditor
            section={modalState.type === 'edit' ? modalState.section : undefined}
            onSave={handleSaveSection}
            onCancel={closeModal}
            isSaving={createMutation.isPending || updateMutation.isPending}
          />
        )}

        {modalState.type === 'publish' && (
          <PublishVersionModal
            onPublish={handlePublish}
            onCancel={closeModal}
            isPublishing={publishMutation.isPending}
          />
        )}
      </div>
    </Layout>
  );
}
